import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import {
  Building2, Users, Wallet, Wrench, TrendingUp, Landmark,
  ShieldCheck, Cloud, Globe, ArrowRight, Check, KeyRound,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { LanguageSelect } from "@/components/LanguageSelect";

const HERO =
  "https://images.unsplash.com/photo-1767884899135-0453e80bfd1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const BENEFIT_IMG =
  "https://images.unsplash.com/photo-1778434842498-1b4f6daac33d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Landing() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [openFeature, setOpenFeature] = useState(null);

  if (user) return <Navigate to="/dashboard" replace />;

  const features = [
    { icon: Building2, title: "物件・部屋管理", desc: "物件カルテ、部屋・空室状況を一元管理。", detail: "物件ごとの基本情報・写真・設備を管理し、部屋単位で入居中／空室のステータスをリアルタイムに把握。ポートフォリオ全体を一目で確認できます。" },
    { icon: Users, title: "入居者・契約管理", desc: "入居者情報と契約更新をまとめて管理。", detail: "入居者のプロフィールと連絡先、契約期間・賃料・更新条件を一元管理。更新期限が近づく契約を自動で可視化します。" },
    { icon: Wallet, title: "家賃請求・入金・滞納", desc: "請求の自動生成から入金消込、滞納督促まで。", detail: "毎月の請求書を自動生成し、入金を消込。滞納が発生した場合はステータス管理と督促の記録まで一気通貫で対応します。" },
    { icon: Wrench, title: "修繕・業者管理", desc: "修繕依頼のワークフローと業者を管理。", detail: "入居者からの修繕依頼を受付から完了まで追跡。協力業者の情報と対応履歴もまとめて管理できます。" },
    { icon: TrendingUp, title: "収支・投資分析", desc: "NOI・キャッシュフロー・利回りを可視化。", detail: "物件ごとのNOI・キャッシュフロー・表面／実質利回りを自動計算。投資判断に役立つ指標をダッシュボードで可視化します。" },
    { icon: Landmark, title: "ローン・書類・監査", desc: "返済予定・書類保管・操作ログを一元化。", detail: "借入の返済予定を管理し、契約書などの重要書類を安全に保管。すべての操作は監査ログに記録され、運用の透明性を担保します。" },
  ];

  const benefits = [
    { icon: Cloud, title: "クラウドで、いつでもどこでも", desc: "スマホ／PC対応。物件情報も収支もリアルタイムに把握できます。" },
    { icon: ShieldCheck, title: "セキュリティを重視した設計", desc: "マルチテナント分離・ロール権限・監査ログで安心の運用。" },
    { icon: Globe, title: "日本語標準・多言語対応", desc: "日本語をベースに英語・ポルトガル語にも対応。" },
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans" data-testid="landing-page">
      {/* Top nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#111111]/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto h-16 px-5 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-11 w-auto object-contain bg-white rounded-md p-1 shrink-0" />
            <div className="leading-tight">
              <div className="font-display font-semibold tracking-wide text-base">NEO REAL ESTATE</div>
              <div className="text-[11.5px] text-gray-500 tracking-wider">MANAGEMENT SAAS</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSelect variant="dark" className="hidden sm:flex" />
            <Link
              to="/login"
              className="text-sm text-gray-300 hover:text-white transition-colors"
              data-testid="landing-nav-login"
            >
              {t("ログイン")}
            </Link>
            <Link
              to="/tenant/login"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors"
              data-testid="landing-nav-tenant-login"
            >
              <KeyRound className="w-4 h-4" />
              {t("借主ログイン")}
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm transition-colors"
              data-testid="landing-nav-cta"
            >
              {t("無料で始める")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d] via-[#0d0d0d]/85 to-[#0d0d0d]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 w-full">
          <div className="max-w-2xl">
            <span className="inline-block text-brand text-xs font-semibold tracking-widest uppercase mb-5" data-testid="hero-eyebrow">
              {t("次世代の不動産管理クラウド")}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-light leading-[1.25] mb-6 [word-break:auto-phrase]" data-testid="hero-title">
              {t("物件・入居者・契約・家賃から")}
              <br />
              <span className="text-brand">{t("収支・投資分析まで、ひとつに。")}</span>
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-9 max-w-xl" data-testid="hero-sub">
              {t("煩雑な賃貸管理業務をクラウドで一元化。マルチテナント・RBAC・監査ログを備え、本番運用に耐える不動産管理SaaSです。")}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-medium px-7 py-3.5 rounded-sm transition-colors"
                data-testid="hero-cta-primary"
              >
                {t("無料で始める")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 hover:bg-white/5 text-white font-medium px-7 py-3.5 rounded-sm transition-colors"
                data-testid="hero-cta-secondary"
              >
                {t("ログインはこちら")}
              </Link>
              <Link
                to="/tenant/login"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-white font-medium px-4 py-3.5 transition-colors"
                data-testid="hero-cta-tenant-login"
              >
                <KeyRound className="w-4 h-4" />
                {t("借主ログイン")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-[86px] border-t border-white/5" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-brand text-xs font-semibold tracking-widest uppercase">{t("主な機能")}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-light mt-3">{t("賃貸経営に必要なすべての機能")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                onClick={() => setOpenFeature(f)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenFeature(f); } }}
                className="group bg-[#161616] border border-white/5 rounded-lg p-7 hover:border-brand/40 hover:-translate-y-1 transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-md bg-brand/15 text-brand flex items-center justify-center mb-5 group-hover:bg-brand group-hover:text-white transition-colors">
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-medium mb-2">{t(f.title)}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{t(f.desc)}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs text-brand opacity-70 group-hover:opacity-100 transition-opacity" data-testid={`feature-more-${i}`}>
                  {t("詳しく見る")}
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-[67px] bg-[#111111]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative rounded-xl overflow-hidden order-last lg:order-first">
            <img src={BENEFIT_IMG} alt="" className="w-full h-full object-cover aspect-[4/3]" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#111111]/70 to-transparent" />
            <div className="absolute -bottom-px -left-px w-40 h-1 bg-brand" />
          </div>
          <div>
            <span className="text-brand text-xs font-semibold tracking-widest uppercase">{t("選ばれる理由")}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-light mt-3 mb-10">{t("運用を、もっとシンプルに。")}</h2>
            <div className="space-y-8">
              {benefits.map((b, i) => (
                <div key={i} className="flex gap-4" data-testid={`benefit-${i}`}>
                  <div className="shrink-0 w-11 h-11 rounded-md bg-brand/15 text-brand flex items-center justify-center">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium mb-1">{t(b.title)}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{t(b.desc)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-[58px]" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-2xl bg-gradient-to-br from-[#1a1a1a] to-[#111111] border border-white/10 p-10 sm:p-16 text-center overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full border border-brand/20" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full border border-white/5" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl font-light mb-4">
                {t("今日から、不動産管理をもっとスマートに。")}
              </h2>
              <p className="text-gray-400 mb-9 max-w-xl mx-auto">
                {t("デモアカウントですぐにお試しいただけます。")}
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-medium px-8 py-3.5 rounded-sm transition-colors"
                data-testid="cta-button"
              >
                {t("無料で始める")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand" />{t("クレジットカード不要")}</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand" />{t("日本語標準")}</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand" />{t("マルチテナント対応")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-9 w-auto object-contain bg-white rounded-md p-1 shrink-0" />
            <div className="text-sm">
              <span className="font-display font-semibold tracking-wide">NEO REAL ESTATE</span>
              <span className="text-gray-500 ml-2 text-xs">{t("不動産管理SaaS")}</span>
            </div>
          </div>
          <div className="text-xs text-gray-500">© 2026 Neo Real Estate Management. {t("すべての権利を留保します。")}</div>
        </div>
      </footer>

      {/* Feature detail dialog */}
      <Dialog open={!!openFeature} onOpenChange={(o) => { if (!o) setOpenFeature(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="feature-dialog">
          {openFeature && (
            <DialogHeader>
              <div className="w-12 h-12 rounded-md bg-brand/15 text-brand flex items-center justify-center mb-3">
                {React.createElement(openFeature.icon, { className: "w-6 h-6" })}
              </div>
              <DialogTitle className="text-xl text-left">{t(openFeature.title)}</DialogTitle>
              <DialogDescription className="text-sm leading-relaxed pt-2 text-left">{t(openFeature.detail)}</DialogDescription>
            </DialogHeader>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
